import React from "react";
import { DataGrid, DateBox, Button as DxButton } from "devextreme-react";
import {
  Column,
  FilterRow,
  HeaderFilter,
  Selection,
  Scrolling,
} from "devextreme-react/data-grid";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, CircularProgress, Tooltip, Alert } from "@mui/material";

import { dataGridConfig } from "../../../constants";
import { DIALOG_MESSAGES, Duplicate_Folder_Error_Message } from "../../../constants";
import { DialogBox } from "../../CreateSites/DialogBox";
import UserCustomPermissionsManager from "../UserCustomPermissionsManager";
import PublicationNotice from "./PublicationNotice";
import BulkUserAssignmentManager from "../BulkUserAssignmentManager";
import { useManageCustomPermissionsLanding } from "../ManageCustomPermissionsLanding.context";
import ManageCustomPermissionsFolderPermissionsPage from "../ManageCustomPermissionsFolderPermissionsPage";
import CustomFolderPermission from "../CustomFolderPermission";

const ManageCustomPermissionsView = () => {
  const {
    isCsFlow,
    folderPermissionChangedCount,
    showPublicationNotice,
    scheduleDetails,
    customPermissionBannerData,
    isloading,
    isButtonDisabled,
    selectedEntityIds,
    entities,
    dataGridRef,
    setSelectedEntityIds,
    cellRenderEntityName,
    cellRenderUsers,
    cellRenderIsRestricted,
    cellRenderStatus,
    cellRenderScheduledDate,
    cellRenderPublishedDate,
    cellRenderForDate,
    cellRenderActions,
    handleDataGridCellPrepared,
    handleOpenBulkUsers,
    handleFolderPermission,
    exportToExcel,
    handleCancel,
    handleSaveChanges,
    isSaveDisabled,
    showUserModel,
    handleUsersModalClose,
    handleUsersChanges,
    selectedLegalEntities,
    isUserlistDirty,
    showBulkUserModel,
    handleBulkUsersChanges,
    isBulkUsersDirty,
    bulkSelectedEntities,
    users,
    bulkEntityUsersMap,
    setBulkEntityUsersMap,
    handleBulkSelectedEntityIdsChange,
    showFolderPermissionModel,
    handleFolderPermissionModalClose,
    handleFolderChanges,
    isRefreshingFolderPermissions,
    isSiteLevelFolderPermission,
    isSiteFolderPermissionDirty,
    isFolderTreeDirty,
    siteID,
    setSelectedLegalEntities,
    refreshManageCustomPermissionsAndReopenFolderPermissions,
    showUnsavedChangesModal,
    handleUnsavedChanges,
    handleAttemptClose,
    showScheduleProcessModal,
    handleScheduleProcess,
    scheduleDate,
    handleScheduleProcessClose,
    isModalFromSaveButton,
    handleSaveWithoutScheduling,
    handleScheduleDate,
    isScheduleDateDisabled,
    annotateScheduleCalendarDisabledDates,
    showCancelProcessModel,
    handleCancelProcess,
    duplicateEntityFolderNames,
    showFolderPermissionView
  } = useManageCustomPermissionsLanding();


  return (
    <section className="hq-page custom-page-shell m-20">
      {showPublicationNotice && scheduleDetails?.status && (
        <PublicationNotice
          customPermissionBannerData={customPermissionBannerData}
          variant={scheduleDetails?.status}
          scheduledDate={scheduleDetails?.scheduleDate}
        />
      )}

      {duplicateEntityFolderNames.length > 0 && (
        <Alert severity="error" sx={{ mb: 1, padding: 1, fontSize: "14px" }}>
          {Duplicate_Folder_Error_Message.replace("{xx}", duplicateEntityFolderNames.join(", "))}
        </Alert>
      )}

      {isloading ? (
        <div className="mcp-loading-wrap">
          <Box className="mcp-loading-box" p={2}>
            <CircularProgress />
          </Box>
        </div>
      ) : showFolderPermissionView ? (
        <>
          <ManageCustomPermissionsFolderPermissionsPage
            isCsFlow={isCsFlow}
            onRefreshRequest={refreshManageCustomPermissionsAndReopenFolderPermissions}
            isRefreshingFolderPermissions={isRefreshingFolderPermissions}
            handleSaveChanges={isCsFlow ? handleSaveChanges : handleFolderChanges}
            handleFolderPermissionModalClose={handleFolderPermissionModalClose}
            title={"Folder Permissions"}
            btnContinueDisabled={isButtonDisabled || isRefreshingFolderPermissions || isSiteLevelFolderPermission}
          />
        </>
      ) : (
        <>
          <div className="custom-page-actions">
            <Button
              variant="outlined"
              size="medium"
              startIcon={<PeopleAltOutlinedIcon className="mcp-button-icon" />}
              onClick={handleOpenBulkUsers}
              className="btn btn-secondary"
              disabled={isButtonDisabled || isloading || selectedEntityIds.length <= 0}
            >
              Manage Users
            </Button>

            <Button
              variant="outlined"
              size="medium"
              startIcon={
                <Tooltip
                  title={
                    folderPermissionChangedCount > 0
                      ? `${folderPermissionChangedCount} modified folder${folderPermissionChangedCount === 1 ? "" : "s"}`
                      : "Folder permissions"
                  }
                  arrow
                  placement="top-end"
                >
                  <Badge
                    badgeContent={folderPermissionChangedCount}
                    color="error"
                    overlap="circular"
                    invisible={folderPermissionChangedCount === 0}
                    className="mcp-folder-permission-badge"
                  >
                    <FolderSharedIcon className="mcp-button-icon" />
                  </Badge>
                </Tooltip>
              }
              onClick={handleFolderPermission}
              className="btn btn-secondary"
            >
              Folder Permissions
            </Button>

            <DxButton
              text="Export"
              hint="Export to Excel"
              icon="exportxlsx"
              onClick={exportToExcel}
              disabled={isloading || !entities || entities.length === 0}
            />
          </div >
          <div className="mcp-hq-page">
            <DataGrid
              {...dataGridConfig}
              height={
                showPublicationNotice && scheduleDetails?.status
                  ? "calc(100vh - 250px)"
                  : "calc(100vh - 140px)"
              }
              dataSource={isloading ? [] : entities}
              wordWrapEnabled={true}
              noDataText={"No data"}
              allowColumnResizing={true}
              ref={dataGridRef}
              showBorders={true}
              showColumnLines={false}
              showRowLines={true}
              keyExpr="entityId"
              onSelectionChanged={(e: any) => {
                setSelectedEntityIds([
                  ...((e?.selectedRowKeys as Array<string | number>) ?? []),
                ]);
              }}
            >
              <Scrolling mode="standard" />
              <FilterRow visible={true} />
              <HeaderFilter visible={true} />
              <Selection mode="multiple" showCheckBoxesMode="always" selectAllMode="allpages" />
              <Scrolling columnRenderingMode="virtual" />
              <Column
                cssClass="cell-text"
                alignment="left"
                dataField="entityId"
                caption="Entity ID"
                width={100}
                headerCellRender={() => (
                  <span className="cell-text-header">Entity ID</span>
                )}
              />
              <Column
                cssClass="cell-text"
                alignment="left"
                dataField="entityName"
                caption={"Entity Name"}
                width={200}
                headerCellRender={() => (
                  <span className="cell-text-header">Entity Name</span>
                )}
                cellRender={cellRenderEntityName}
              ></Column>
              <Column
                alignment="left"
                dataField="numberOfUsers"
                caption={"Users"}
                headerCellRender={() => <span className="cell-text-header">Users</span>}
                cellRender={cellRenderUsers}
              ></Column>
              <Column
                alignment="left"
                dataField="isRestricted"
                caption="Restricted"
                headerCellRender={() => (
                  <span className="cell-text-header">Restricted</span>
                )}
                cellRender={cellRenderIsRestricted}
              ></Column>
              <Column
                cssClass="cell-text"
                alignment="left"
                caption="Status"
                width={130}
                headerCellRender={() => (
                  <span className="cell-text-header">Status</span>
                )}
                cellRender={cellRenderStatus}
              />
              <Column
                cssClass="cell-text"
                alignment="left"
                caption="Scheduled"
                headerCellRender={() => (
                  <span className="cell-text-header">Scheduled</span>
                )}
                cellRender={cellRenderScheduledDate}
              />
              <Column
                cssClass="cell-text"
                alignment="left"
                caption="Published"
                headerCellRender={() => (
                  <span className="cell-text-header">Published</span>
                )}
                cellRender={cellRenderPublishedDate}
              />
              <Column
                cssClass="cell-text"
                dataField="dateModified"
                caption={"Date Modified"}
                headerCellRender={() => (
                  <span className="cell-text-header">Date Modified</span>
                )}
                cellRender={cellRenderForDate}
              ></Column>
              <Column
                cssClass="cell-text"
                dataField="lastModifiedBy"
                caption="Last Modified by"
                width={200}
                headerCellRender={() => (
                  <span className="cell-text-header">Last Modified by</span>
                )}
              />
              <Column
                type="buttons"
                caption={"Actions"}
                width={100}
                headerCellRender={() => (
                  <span className="cell-text-header">Actions</span>
                )}
                cellRender={cellRenderActions}
              />
            </DataGrid>
          </div>
          <div className="flex-end-container">
            <Button
              onClick={handleCancel}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChanges}
              variant="contained"
              disabled={isSaveDisabled()}
              className={isSaveDisabled() ? "mcp-save-button-disabled" : undefined}
            >
              Save Changes
            </Button>
          </div>
        </>
      )}

      <DialogBox
        isDisplayModal={showUserModel}
        handleClose={handleUsersModalClose}
        btnOpenText={"Save"}
        maxWidth={"sm"}
        handleContinue={handleUsersChanges}
        title={`${selectedLegalEntities?.entityName} Users`}
        btnContinueDisable={isButtonDisabled || !isUserlistDirty}
      >
        <UserCustomPermissionsManager
          selectedLegalEntities={selectedLegalEntities}
          isEntityUserlevel={true}
        />
      </DialogBox>

      <DialogBox
        isDisplayModal={showBulkUserModel}
        handleClose={handleUsersModalClose}
        btnOpenText={"Save"}
        maxWidth={"md"}
        handleContinue={handleBulkUsersChanges}
        title={`Manage Users`}
        btnContinueDisable={isButtonDisabled || !isBulkUsersDirty}
      >
        <BulkUserAssignmentManager
          selectedEntities={bulkSelectedEntities}
          allEntities={entities}
          onSelectedEntityIdsChange={handleBulkSelectedEntityIdsChange}
          allUsers={users}
          entityUsersMap={bulkEntityUsersMap}
          setEntityUsersMap={setBulkEntityUsersMap}
        />
      </DialogBox>

      <DialogBox
        isDisplayModal={showFolderPermissionModel}
        handleClose={handleFolderPermissionModalClose}
        btnOpenText={"Save"}
        maxWidth={"lg"}
        handleContinue={handleFolderChanges}
        title={
          isSiteLevelFolderPermission
            ? `Folder Permissions`
            : `${selectedLegalEntities?.entityName} Folder Permissions`
        }
        btnContinueDisable={
          isButtonDisabled ||
          isRefreshingFolderPermissions ||
          (isSiteLevelFolderPermission ? !isSiteFolderPermissionDirty : !isFolderTreeDirty)
        }
      >
        <CustomFolderPermission
          isSiteLevelFolderPermission={isSiteLevelFolderPermission}
          selectedLegalEntities={selectedLegalEntities}
          setSelectedLegalEntities={setSelectedLegalEntities}
          siteId={siteID}
          onRefreshRequest={refreshManageCustomPermissionsAndReopenFolderPermissions}
          isRefreshing={isRefreshingFolderPermissions}
        />
      </DialogBox>

      <DialogBox
        maxWidth={"xs"}
        title={DIALOG_MESSAGES.DISCARD_CHANGES.TITLE}
        btnOpenText={DIALOG_MESSAGES.DISCARD_CHANGES.BTN_YES}
        isDisplayModal={showUnsavedChangesModal}
        handleContinue={handleUnsavedChanges}
        handleClose={handleAttemptClose}
      >
        <div className="mcp-unsaved-dialog-body">
          <span className="mcp-unsaved-dialog-text">
            {DIALOG_MESSAGES.DISCARD_CHANGES.BODY_TEXT}
          </span>
        </div>
      </DialogBox>

      <DialogBox
        maxWidth="sm"
        title={DIALOG_MESSAGES.SCHEDULE_PROCESS.TITLE}
        btnOpenText={DIALOG_MESSAGES.SCHEDULE_PROCESS.BTN_SCHEDULE}
        btnEditText={DIALOG_MESSAGES.SCHEDULE_PROCESS.BTN_SAVE_WITHOUT}
        isDisplayModal={showScheduleProcessModal}
        btnCloseTop={false}
        handleContinue={handleScheduleProcess}
        btnContinueDisable={!scheduleDate}
        handleClose={handleScheduleProcessClose}
        handleEdit={isModalFromSaveButton ? handleSaveWithoutScheduling : handleScheduleProcessClose}
      >
        <div className="dialog-body">
          <p className="dialog-text">{DIALOG_MESSAGES.SCHEDULE_PROCESS.BODY_TEXT_1}</p>

          <p className="dialog-text">{DIALOG_MESSAGES.SCHEDULE_PROCESS.BODY_TEXT_2}</p>

          <div className="dialog-section">
            <p className="dialog-section-title">
              {DIALOG_MESSAGES.SCHEDULE_PROCESS.SECTION_TITLE}
            </p>

            <DateBox
              width={300}
              onValueChanged={(e) => handleScheduleDate(e.value)}
              type="date"
              placeholder="(MM/DD/YYYY)"
              min={new Date()}
              disabledDates={(data: any) => isScheduleDateDisabled(data?.date)}
              dropDownOptions={{
                showTitle: false,
                onShown: annotateScheduleCalendarDisabledDates,
              }}
            />
          </div>
        </div>
      </DialogBox>

      <DialogBox
        maxWidth={"xs"}
        title={DIALOG_MESSAGES.CANCEL_PROCESS.TITLE}
        btnOpenText={DIALOG_MESSAGES.CANCEL_PROCESS.BTN_CONFIRM}
        btnCloseText={DIALOG_MESSAGES.CANCEL_PROCESS.BTN_BACK}
        isDisplayModal={showCancelProcessModel}
        btnCloseTop={false}
        handleContinue={handleCancelProcess}
        handleClose={handleScheduleProcessClose}
        icon={<InfoOutlinedIcon className="mcp-dialog-title-icon" />}
        dialogTitleSx={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        <div className="dialog-body">
          <p className="dialog-text">{DIALOG_MESSAGES.CANCEL_PROCESS.BODY_TEXT_1}</p>
          <p className="dialog-text">{DIALOG_MESSAGES.CANCEL_PROCESS.BODY_TEXT_2}</p>
        </div>
      </DialogBox>
    </section >
  );
};

export default ManageCustomPermissionsView;
